import type { Vec3 } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// L'IMU: gravità per il gate di posa (scioglie le due soluzioni IPPE, A N3) e velocità angolare per l'anello di
// stabilità (tempo fermo, non fotogrammi).
//   DeviceMotion (telefono dritto): x destra, y su, z verso l'utente.   OpenCV: x destra, y giù, z avanti (fuori dal retro).
// `accelerationIncludingGravity` su Android è la forza che SI OPPONE alla gravità (pistola dritta: y ≈ +9,8, cioè punta
// "su"), non la gravità. La gravità nel sistema della fotocamera è quindi (−x, +y, +z): MISURATO sulla EDA61K il 13/10/2026
// per y e z (fotocamera inclinata di ~33° verso il basso: grezzo y +8,31, z +5,36; in avanti e in giù la gravità è positiva).
// Per x il segno viene dalla stessa fisica (lato destro in basso → x del sensore negativo → gravità verso destra, +x):
// non ancora misurato sul dispositivo. Il video e il sensore hanno gli stessi assi (immagine dritta con la pistola dritta).
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** La gravità nel sistema della fotocamera OpenCV a partire da `accelerationIncludingGravity` di Android (forza opposta alla gravità). */
export function gravityInCamera(a: Vec3): Vec3 {
  return { x: -a.x, y: a.y, z: a.z };
}

export interface ImuHandle {
  /** Gravità nel sistema della fotocamera, m/s², media mobile; null finché non arriva un evento. */
  gravity(): Vec3 | null;
  /** Velocità angolare in °/s (norma), media mobile. */
  angularSpeed(): number;
  stop(): void;
}

export async function requestMotionPermission(): Promise<boolean> {
  const dme = DeviceMotionEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
  if (typeof dme.requestPermission === 'function') {
    try {
      return (await dme.requestPermission()) === 'granted';
    } catch {
      return false;
    }
  }
  return true;
}

export function startImu(alpha = 0.2): ImuHandle {
  let g: Vec3 | null = null;
  let w = 0;
  const onMotion = (e: DeviceMotionEvent): void => {
    const a = e.accelerationIncludingGravity;
    if (a && a.x !== null && a.y !== null && a.z !== null) {
      const mapped = gravityInCamera({ x: a.x, y: a.y, z: a.z });
      g = g ? { x: g.x + alpha * (mapped.x - g.x), y: g.y + alpha * (mapped.y - g.y), z: g.z + alpha * (mapped.z - g.z) } : mapped;
    }
    const r = e.rotationRate;
    if (r && r.alpha !== null && r.beta !== null && r.gamma !== null) {
      const norm = Math.hypot(r.alpha, r.beta, r.gamma);
      w += alpha * (norm - w);
    }
  };
  window.addEventListener('devicemotion', onMotion);
  return {
    gravity: () => g,
    angularSpeed: () => w,
    stop: () => window.removeEventListener('devicemotion', onMotion),
  };
}

/** L'anello: pieno quando la pistola è stata sotto soglia per holdMs consecutivi. Restituisce 0..1. */
export class StabilityRing {
  private since: number | null = null;

  constructor(
    private readonly thresholdDegPerS = 15,
    private readonly holdMs = 800,
  ) {}

  update(angularSpeedDegPerS: number, nowMs: number): number {
    if (angularSpeedDegPerS > this.thresholdDegPerS) {
      this.since = null;
      return 0;
    }
    this.since ??= nowMs;
    return Math.min(1, (nowMs - this.since) / this.holdMs);
  }
}
